import { ImportExport, KeyboardArrowDown, KeyboardArrowUp } from '@mui/icons-material'
import { Box, Checkbox, Collapse, IconButton, TableCell, Tooltip, Typography, useTheme } from '@mui/material'
import React, { useEffect, useRef, useState } from 'react'
import { getDurationString } from '../../../../store/utils/worklogUtils'
import { WorkEntry } from '../../../../store/types'
import { StyledWorklogEntryTableRow } from '../../../../styles/WorklogTableStyles'
import { t } from 'i18next'

type EntryTableRowProps = {
  entry: WorkEntry
  editMode: boolean
  setEditMode: React.Dispatch<React.SetStateAction<boolean>>
  editedEntry: WorkEntry | null
  setEditedEntry: React.Dispatch<React.SetStateAction<WorkEntry | null>>
  isReadOnly: boolean
  handleClick: (event: React.MouseEvent<HTMLButtonElement>, workEntry: WorkEntry) => void
  isSelected: (workEntry: WorkEntry) => boolean
}

const EntryTableRow = ({
  entry,
  editMode,
  setEditMode,
  editedEntry,
  setEditedEntry,
  isReadOnly,
  handleClick,
  isSelected,
}: EntryTableRowProps) => {
  const theme = useTheme()
  // STATE
  const [open, setOpen] = useState(false)

  // SX
  const textEllipsis = { overflow: 'hidden', whiteSpace: 'nowrap', textOverflow: 'ellipsis' }

  // EDIT MODE
  const [isEdited, setIsEdited] = useState(entry.id === editedEntry?.id)
  const handleEdit = () => {
    setEditedEntry(entry)
    setEditMode(true)
  }

  useEffect(() => {
    setIsEdited(entry.id === editedEntry?.id)
  }, [editedEntry, entry.id])

  useEffect(() => {
    if (!editMode) setIsEdited(false)
  }, [editMode])

  const descriptionRef = useRef<HTMLDivElement | null>(null)
  const [isTextWrapped, setIsTextWrapped] = useState(false)

  useEffect(() => {
    const checkTextWrap = () => {
      if (descriptionRef.current) {
        const isWrapped = descriptionRef.current.scrollWidth > descriptionRef.current.offsetWidth
        setIsTextWrapped(isWrapped)
      }
    }

    checkTextWrap()

    window.addEventListener('resize', checkTextWrap)

    return () => {
      window.removeEventListener('resize', checkTextWrap)
    }
  }, [open, entry.description])

  return (
    <StyledWorklogEntryTableRow
      selected={isEdited}
      className={entry.remote ? 'remote' : ''}
      sx={{
        minHeight: '40px',
        height: '100%',
        bgcolor: isEdited ? 'primary.light' : 'transparent',
        color: isEdited ? 'primary.contrastText' : '',
      }}
    >
      <TableCell
        sx={{
          padding: 0,
          display: 'flex',
          height: '100%',
          alignItems: 'center',
          width: isReadOnly ? '0px' : undefined,
          minWidth: isReadOnly ? 0 : undefined,
          overflow: 'hidden',
        }}
      >
        {!isReadOnly && (
          <>
            <label htmlFor={`checkbox-${entry.id}`} style={{ margin: 0 }}>
              <span style={{ display: 'none' }}>{t('selectEntry', { projectName: entry.project.name })}</span>
            </label>
            <Checkbox
              id={`checkbox-${entry.id}`}
              disabled={isReadOnly}
              onClick={event => handleClick(event, entry)}
              checked={isSelected(entry)}
              aria-label={t('selectEntry', { projectName: entry.project.name })}
            />
            {entry.fromImport && <ImportExport sx={{ color: theme.palette.info.main, fontSize: 16 }} />}
          </>
        )}
      </TableCell>
      <TableCell
        onClick={isReadOnly ? undefined : handleEdit}
        sx={{
          cursor: isReadOnly ? 'default' : 'pointer',
          py: isReadOnly ? 1 : undefined,
          pl: isReadOnly && entry.fromImport ? 1.5 : undefined,
          p: isReadOnly ? undefined : 0,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          {entry.fromImport && <ImportExport sx={{ color: theme.palette.info.main, fontSize: 16 }} />}
          <Tooltip title={entry.project.name} placement="right">
            <Typography variant="body2" sx={{ ...textEllipsis, flex: 1, minWidth: 0 }}>
              {entry.project.name}
            </Typography>
          </Tooltip>
        </Box>
      </TableCell>
      <TableCell onClick={isReadOnly ? undefined : handleEdit} sx={{ cursor: isReadOnly ? 'default' : 'pointer' }}>
        <Collapse orientation="vertical" in={open} collapsedSize={18}>
          <Typography
            variant="body2"
            ref={descriptionRef}
            sx={{
              inlineSize: 'auto',
              overflowWrap: 'break-word',
              textOverflow: open ? '' : 'ellipsis',
              overflow: open ? '' : 'hidden',
              whiteSpace: open ? '' : 'nowrap',
            }}
          >
            {entry.description}
          </Typography>
        </Collapse>
      </TableCell>
      <TableCell
        align="center"
        onClick={isReadOnly ? undefined : handleEdit}
        sx={{ cursor: isReadOnly ? 'default' : 'pointer' }}
      >
        {getDurationString(entry.hours)}
      </TableCell>

      <TableCell sx={{ width: '20px', padding: 0 }} align="center">
        {(isTextWrapped || open) && (
          <IconButton size="small" onClick={() => setOpen(!open)}>
            {open ? (
              <KeyboardArrowUp sx={{ width: 14, height: 14 }} />
            ) : (
              <KeyboardArrowDown sx={{ width: 14, height: 14 }} />
            )}
          </IconButton>
        )}
      </TableCell>
    </StyledWorklogEntryTableRow>
  )
}

export default EntryTableRow
